import React, { Key, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { debounce } from 'lodash';
import Axios from 'axios';
import {
  ESLockbox,
  LockboxTypes,
  LockboxType,
  lockboxTypeMap,
} from '@bb/domain/dist/types';
import {
  Avatar,
  Button,
  Checkbox,
  EnhancedFilterDropdown,
  Input,
  Link,
  ListingImage,
  TableProps,
  Tag,
  Text,
  theme,
} from 'components';
import { BrandLogo } from 'components/BrandLogo';
import { AuthContext } from 'contexts';
import AddNewLockbox from '@app/ManageLockboxPage/AddNewLockbox';
import { Loading } from '@app/react-common';
import { LockboxVendorSettings } from '@bb/automaton';
import { isTeamAdmin } from '@bb/users';
import SearchTable from './SearchTable';
import { useLockboxesContext } from './LockboxesContext';
import {
  StyledFilterBottom,
  StyledFilterListing,
  StyledFilterSearchResult,
  StyledFilterUser,
} from '../LockboxesActivityPage/LockboxActivityTable';

const filterConfirmButtonText = 'Apply';
const filterResetButtonText = 'Reset';

const Unassigned: React.FC = () => (
  <div style={{ color: theme.alpha45 }}>Unassigned</div>
);

const LockboxesTable: React.FC = () => {
  const { auth } = React.useContext(AuthContext);

  const shouldDisplaySupraFilters =
    auth.supraSubscription?.status === 'subscribed' ||
    auth.isAdmin ||
    isTeamAdmin(auth);

  const history = useHistory();
  const {
    lockboxes,
    currentLockbox,
    setCurrentLockbox,
    total,
    loading,
    setLoading,
    setPage,
    sort,
    setSort,
    showTable,
    setShowTable,
    lockboxTypeFilter,
    setLockboxTypeFilter,
    selectedListings,
    setSelectedListings,
    selectedOwners,
    setSelectedOwners,
    selectedAssignee,
    setSelectedAssignee,
    deleteLockbox,
    setSelectedLockboxes,
    onLockboxAdded,
  } = useLockboxesContext();

  const [filterSearchLoading, setFilterSearchLoading] = React.useState(false);
  const [searchedListings, setSearchedListings] = React.useState<
    NonNullable<ESLockbox['listing']>[]
  >([]);

  const handleReset = (setSelectedKeys: (keys: string[]) => void) => {
    setSelectedKeys([]);
  };

  const [searchedOwners, setSearchedOwners] = useState<
    NonNullable<ESLockbox['owner']>[]
  >([]);
  const [searchedAssignee, setSearchedAssignee] = useState<
    NonNullable<ESLockbox['owner']>[]
  >([]);

  const debouncedOwnerSearch = useRef(
    debounce(async value => {
      if (!value) {
        setSearchedOwners([]);
        return;
      }
      setFilterSearchLoading(true);
      try {
        const { data: owners } = await Axios.get(
          `lockbox/filters/owners/${encodeURIComponent(value)}`,
        );
        setSearchedOwners(owners);
      } finally {
        setFilterSearchLoading(false);
      }
    }, 500),
  ).current;

  const debouncedAssigneeSearch = React.useRef(
    debounce(async value => {
      if (!value) {
        setSearchedAssignee([]);
        return;
      }
      setFilterSearchLoading(true);
      const { data: users } = await Axios.get(
        `lockbox/filters/users/${encodeURIComponent(value)}`,
      );
      setSearchedAssignee(users);
      setFilterSearchLoading(false);
    }, 500),
  ).current;

  const debouncedListingSearch = React.useRef(
    debounce(async value => {
      if (!value) {
        setSearchedListings([]);
        return;
      }
      setFilterSearchLoading(true);
      const { data: listings } = await Axios.get(
        `lockbox/filters/listings/${value}`,
      );
      setSearchedListings(listings);
      setFilterSearchLoading(false);
    }, 500),
  ).current;

  const StyledTextFilter = ({
    id,
    selectedKeys,
    setSelectedKeys,
    text = '(Blank)',
    allKeys,
  }: {
    id: Key;
    selectedKeys: Key[];
    setSelectedKeys: (value: Key[]) => void;
    text?: string;
    allKeys?: Key[];
  }) => (
    <StyledFilterSearchResult>
      <Checkbox
        checked={selectedKeys.includes(id)}
        value={id}
        onChange={() => {
          setSelectedKeys(
            selectedKeys.includes(id)
              ? selectedKeys.filter((key: string) => key !== id)
              : [...selectedKeys, id],
          );
        }}
      />
      <Text>{text}</Text>
    </StyledFilterSearchResult>
  );

  const columns: TableProps['columns'] = ([
    {
      title: 'Box Type',
      dataIndex: 'lockboxType',
      key: 'lockboxType',
      width: 140,
      filters: LockboxTypes.filter(
        lockboxType =>
          (shouldDisplaySupraFilters ||
            !['iBox', 'invoy'].includes(lockboxType)) &&
          (!auth.org.lockboxVendorSettings ||
            auth.org.lockboxVendorSettings![
              (['iBox', 'invoy'].includes(lockboxType)
                ? 'supra'
                : lockboxType) as keyof LockboxVendorSettings
            ]),
      ).map(lockboxType => ({
        text: lockboxTypeMap[lockboxType],
        value: lockboxType,
      })),
      filteredValue: lockboxTypeFilter,
      filterDropdown: props => (
        <EnhancedFilterDropdown {...props} okText="Apply" resetText="Clear" />
      ),
      render: (lockboxType: LockboxType) => (
        <BrandContainer>
          <BrandLogo brand={lockboxType} />
          <EmphasisText>{lockboxTypeMap[lockboxType]}</EmphasisText>
        </BrandContainer>
      ),
    },
    {
      title: 'Serial',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      width: 170,
      sorter: true,
      sortOrder: sort?.field === 'serialNumber' ? `${sort.direction}end` : null,
      render: (serialNumber: string, lockbox: ESLockbox) => (
        <SerialNumberContainer>
          {serialNumber}
          {lockbox.assignee?.id !== auth.id &&
            lockbox.owner?.id !== auth.id &&
            lockbox.granteeUserIds?.includes(auth.id) && (
              <Tag color="default">Shackle Permissions</Tag>
            )}
        </SerialNumberContainer>
      ),
    },
    {
      title: 'Listing',
      dataIndex: 'listing',
      key: 'listing',
      width: 250,
      filteredValue: Object.keys(selectedListings),
      filterDropdown: ({
        confirm,
        setSelectedKeys,
        selectedKeys,
        clearFilters,
      }) => {
        const allKeys = [
          ...Object.values(selectedListings).map(item => item.id),
          ...searchedListings.map(item => item.id),
          '(Blank)',
        ];
        return (
          <div style={{ padding: 8 }}>
            <Input
              placeholder={`Search Listings`}
              onChange={element => debouncedListingSearch(element.target.value)}
              allowClear
            />
            {filterSearchLoading && <Loading />}
            <StyledTextFilter
              id="(Blank)"
              text={'Unassigned'}
              selectedKeys={selectedKeys}
              setSelectedKeys={setSelectedKeys}
              allKeys={allKeys}
            />
            {searchedListings.length
              ? searchedListings.map(searchedListing => (
                  <StyledFilterListing
                    key={searchedListing.id}
                    listing={searchedListing}
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                  />
                ))
              : Object.values(selectedListings)
                  .filter(o => o.id !== '(Blank)')
                  .map(selectedListing => (
                    <StyledFilterListing
                      key={selectedListing.id}
                      listing={selectedListing}
                      selectedKeys={selectedKeys}
                      setSelectedKeys={setSelectedKeys}
                    />
                  ))}
            <StyledFilterBottom>
              <Button
                size="small"
                type="link"
                disabled={!selectedKeys.length}
                onClick={clearFilters}
              >
                Reset
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  confirm();
                }}
                size="small"
              >
                OK
              </Button>
            </StyledFilterBottom>
          </div>
        );
      },
      render: listing => {
        if (!listing) return <Unassigned />;
        const { thumbnail, address } = listing;
        return (
          <StyledContainer>
            <ListingImage imageSrc={thumbnail} size={24} fixProportion />
            {address}
          </StyledContainer>
        );
      },
    },
    {
      title: 'Assignee',
      dataIndex: 'assignee',
      key: 'assignee',
      width: 220,
      filteredValue: Object.keys(selectedAssignee),
      filterDropdown: ({
        confirm,
        setSelectedKeys,
        selectedKeys,
        clearFilters,
      }) => {
        const allKeys = [
          ...Object.values(selectedAssignee).map(user => user.id),
          ...searchedOwners.map(user => user.id),
          '(Blank)',
        ];
        return (
          <PaddedDropdownContainer>
            <Input
              placeholder={`Search Users`}
              onChange={element =>
                debouncedAssigneeSearch(element.target.value)
              }
              allowClear
            />
            <StyledTextFilter
              id="(Blank)"
              text={'Unassigned'}
              selectedKeys={selectedKeys}
              setSelectedKeys={setSelectedKeys}
              allKeys={allKeys}
            />
            {[
              ...Object.values(selectedAssignee)
                .filter(o => o.id !== '(Blank)')
                .map(selectedUser => (
                  <StyledFilterUser
                    key={selectedUser.id}
                    user={selectedUser}
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    hideAvatar
                    allKeys={allKeys}
                  />
                )),
              ...searchedAssignee
                .filter(so => !selectedAssignee[so.id])
                .map(searchedAggignee => (
                  <StyledFilterUser
                    key={searchedAggignee.id}
                    user={searchedAggignee}
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    hideAvatar
                    allKeys={allKeys}
                  />
                )),
            ]}
            {filterSearchLoading && <Loading />}
            <StyledFilterBottom>
              <Button
                size="small"
                type="link"
                disabled={!selectedKeys.length}
                onClick={() => {
                  handleReset(setSelectedKeys);
                }}
              >
                {filterResetButtonText}
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  confirm();
                }}
                size="small"
              >
                {filterConfirmButtonText}
              </Button>
            </StyledFilterBottom>
          </PaddedDropdownContainer>
        );
      },
      render: assignee => {
        if (!assignee) return <Unassigned />;
        return (
          <StyledContainer>
            <AvatarContainer>
              <Avatar
                user={
                  assignee.firstName
                    ? assignee
                    : { ...assignee, firstName: assignee.name }
                }
                width={32}
              />
            </AvatarContainer>

            {assignee?.firstName || assignee?.lastname
              ? `${assignee?.firstName} ${assignee?.lastName}`
              : `${assignee.name}`}
          </StyledContainer>
        );
      },
    },
    {
      title: 'Owner',
      dataIndex: 'owner',
      key: 'owner',
      width: 220,
      filteredValue: Object.keys(selectedOwners),
      filterDropdown: ({
        confirm,
        setSelectedKeys,
        selectedKeys,
        clearFilters,
      }) => {
        const allKeys = [
          ...Object.values(selectedOwners).map(owner => owner.id),
          ...searchedOwners.map(owner => owner.id),
          '(Blank)',
        ];
        return (
          <PaddedDropdownContainer>
            <Input
              placeholder={`Search Owners`}
              onChange={element => debouncedOwnerSearch(element.target.value)}
              allowClear
            />
            <StyledTextFilter
              id="(Blank)"
              text={'Unassigned'}
              selectedKeys={selectedKeys}
              setSelectedKeys={setSelectedKeys}
              allKeys={allKeys}
            />
            {[
              ...Object.values(selectedOwners)
                .filter(o => o.id !== '(Blank)')
                .map(selectedOwner => (
                  <StyledFilterUser
                    key={selectedOwner.id}
                    user={selectedOwner}
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    hideAvatar
                    allKeys={allKeys}
                  />
                )),
              ...searchedOwners
                .filter(so => !selectedOwners[so.id])
                .map(searchedOwner => (
                  <StyledFilterUser
                    key={searchedOwner.id}
                    user={searchedOwner}
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    hideAvatar
                    allKeys={allKeys}
                  />
                )),
            ]}
            {filterSearchLoading && <Loading />}
            <StyledFilterBottom>
              <Button
                size="small"
                type="link"
                disabled={!selectedKeys.length}
                onClick={() => {
                  handleReset(setSelectedKeys);
                }}
              >
                {filterResetButtonText}
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  confirm();
                }}
                size="small"
              >
                {filterConfirmButtonText}
              </Button>
            </StyledFilterBottom>
          </PaddedDropdownContainer>
        );
      },
      render: owner => {
        if (!owner) return <Unassigned />;
        return (
          <StyledContainer>
            <AvatarContainer>
              <Avatar
                user={
                  owner.firstName ? owner : { ...owner, firstName: owner.name }
                }
                width={32}
              />
            </AvatarContainer>
            {owner?.firstName || owner?.lastname
              ? `${owner?.firstName} ${owner?.lastName}`
              : `${owner.name}`}
          </StyledContainer>
        );
      },
    },
    {
      title: 'Actions',
      width: 220,
      render: lockbox => {
        // Actions for Supra Lockboxes are handled on the Supra Lockbox Details page
        if (lockbox.brand === 'supraLockbox') return <Link>Details</Link>;

        return (
          <ActionsContainer>
            <Link
              onClick={e => e.stopPropagation()}
              href={`#/lockboxes/${lockbox.brand.toLowerCase()}/${
                lockbox.serialNumber
              }/accesslogs`}
            >
              Access Logs
            </Link>
            <Link
              onClick={e => {
                e.stopPropagation();
                deleteLockbox(lockbox);
              }}
            >
              Delete
            </Link>
          </ActionsContainer>
        );
      },
    },
  ] as TableProps['columns'])!.filter(Boolean);

  return (
    <>
      {!showTable ? (
        <AddNewLockbox
          // @ts-expect-error
          auth={auth}
          lockbox={currentLockbox}
          setShowTable={setShowTable}
          setIsLoading={setLoading}
          onLockboxAdded={onLockboxAdded}
        />
      ) : (
        <SearchTable
          rowKey={record => record._id}
          columns={columns}
          loading={loading}
          dataSource={lockboxes ?? []}
          totalItems={total || 0}
          onRow={(lockbox: ESLockbox) => ({
            ...(lockbox.brand === 'supraLockbox'
              ? {
                  onClick: () => {
                    history.push(`/lockbox/${lockbox._id}`);
                  },
                }
              : {
                  onClick: () => {
                    setCurrentLockbox(lockbox);
                    setShowTable(false);
                  },
                }),
            'data-cy': `lockbox-row-${lockbox._id}`,
          })}
          onChange={(_, filters, sorter) => {
            // Set filter values
            const toBeSelectedListings: Record<
              string,
              NonNullable<ESLockbox['listing']>
            > = {};
            searchedListings.forEach(searchedListing => {
              if (
                filters.listing?.find(
                  filteredListing => filteredListing === searchedListing.id,
                )
              ) {
                toBeSelectedListings[searchedListing.id] = searchedListing;
              }
            });
            if (filters.listing?.includes('(Blank)')) {
              // @ts-expect-error blank listing for unassigned
              toBeSelectedListings['(Blank)'] = { id: '(Blank)' };
            }

            const newSelectedOwners: any = {};
            searchedOwners.forEach(searchedOwner => {
              if (
                filters.owner?.find(
                  filterOwner => filterOwner === searchedOwner.id,
                )
              ) {
                newSelectedOwners[searchedOwner.id] = searchedOwner;
              }
            });
            if (filters.owner?.includes('(Blank)')) {
              newSelectedOwners['(Blank)'] = { id: '(Blank)' };
            }

            const newSelectedAssignee: any = {};
            searchedAssignee.forEach(user => {
              if (
                filters.assignee?.find(filterOwner => filterOwner === user.id)
              ) {
                newSelectedAssignee[user.id] = user;
              }
            });
            if (filters.assignee?.includes('(Blank)')) {
              newSelectedAssignee['(Blank)'] = { id: '(Blank)' };
            }

            setSelectedOwners(newSelectedOwners);
            setSelectedAssignee(newSelectedAssignee);
            setSelectedListings(toBeSelectedListings);
            setLockboxTypeFilter(filters.lockboxType as LockboxType[]);

            // Set sorter
            if ((sorter as any).field && (sorter as any).order) {
              setSort({
                field: (sorter as any).field,
                direction: (sorter as any).order === 'descend' ? 'desc' : 'asc',
              });
            } else {
              setSort(null);
            }

            setPage(1);
          }}
          rowSelection={{
            type: 'checkbox',
            onChange: (_, selectedRows) => {
              if (!selectedRows.length) {
                setSelectedLockboxes([]);
              } else {
                setSelectedLockboxes(
                  selectedRows
                    .filter(lockbox => lockbox.brand === 'supraLockbox')
                    .map(({ _id }) => _id),
                );
              }
            },
            getCheckboxProps: record => ({ id: record.id }),
          }}
        />
      )}
    </>
  );
};

// #region Styled components
const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const AvatarContainer = styled.div`
  min-width: 32px;
`;

const BrandContainer = styled(StyledContainer)`
  gap: 13px;
`;

const SerialNumberContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const EmphasisText = styled.div`
  font-weight: 500;
`;

const ActionsContainer = styled.div`
  display: flex;
  gap: 40px;
`;
// #endregion

const PaddedDropdownContainer = styled.div`
  padding: 8px;
`;

export default LockboxesTable;
